'use client';

import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useSearchParams } from 'next/navigation';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useInitialParams } from '../InitialParamsProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useOffersContext } from '../OffersProvider';
import { useApexCalendarFilters } from '../evo/trip-overview/ApexCalendarFiltersProvider';
import {
  buildApexScopeKey,
  getApexPriceOverrides,
  removeApexPriceOverride,
  setApexPriceOverride,
  type ApexPriceOverride,
} from '../evo/trip-overview/apexPriceOverrideStorage';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';
import { BASE_PATH } from '../../search/utils/path';
import { getLoggingInfo } from '../lib/loggingInfo';

// Prices are compared to the cent to avoid floating point noise triggering spurious overrides.
const sameToCents = (a: number, b: number) => Math.round(a * 100) === Math.round(b * 100);

export type ApexCalendarData = {
  products: Map<string, ApexCalendarProduct>;
  /**
   * Days the rooms endpoint reported sold out (yyyy-MM-dd). They are removed from `products`, so a
   * day is either priced or unavailable, never both — that is what keeps a sold-out day out of the
   * cheapest-price maths. (MPH-887)
   */
  unavailableDates: Set<string>;
  cheapestPrice: number;
  currency: string;
  isLoading: boolean;
};

export function useApexCalendar(): ApexCalendarData {
  const { internalHotelId, pageType, priceType, setApexCalendarReady, setPriceContext } = useInitialParams();
  // `nights` here is the current stay's length; the calendar-filter nights (below) is what the apex
  // data was fetched for — the two diverge when the dates change through something other than the
  // calendar (sold-out alternative dates are queried at nights ±1).
  const { adults, checkin, nights: stayNights, travellerGroups, totalGuestsWithoutInfants } = useUrlSearchParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const searchParams = useSearchParams();
  const origin = searchParams.get(URLParameters.ORIGIN) ?? '';
  const { nights, mealPlans, flightTimesOutbound, flightTimesInbound } = useApexCalendarFilters();

  // The rooms endpoint loads after the calendar and holds the real cheapest bookable price for the
  // currently selected day; it is used to override the apex price for that day (see effect below).
  const { offersData, isLoadingRooms } = useOffersContext();

  // Apex does not support multi-room, so flatten children ages across rooms into a single list.
  const childrenAges = travellerGroups.flatMap((group) => group.childrenAges).join(',');
  const {
    apexFlightsCheapestEnabled,
    apexPriceOverrideDisabled,
    apexPriceOverrideStorageDisabled,
    apexCacheMaxAge,
    apexBestScoreTolerance,
  } = localization.configValuesApp;

  // Both features are on by default; the config flags are kill switches. The override maps the rooms
  // endpoint's per-person price onto the apex per-person price, so it only applies when the rooms price
  // is genuinely per-person. In TOTAL searches (hotel-only) finalPrice is a total, not per-person, so
  // overriding is disabled to avoid showing a total as a per-person price.
  const overrideEnabled = !apexPriceOverrideDisabled && priceType === 'PER_PERSON';
  const persistOverrides = !apexPriceOverrideStorageDisabled;

  // Identity of this apex search: also used to scope the persisted price overrides.
  const scopeKey = buildApexScopeKey({
    internalHotelId,
    origin,
    adults,
    childrenAges,
    nights,
    currency: localization.currency,
    businessProfile: localization.businessProfile,
    mealPlans,
    flightTimesOutbound,
    flightTimesInbound,
    apexFlightsCheapestEnabled,
  });

  const key =
    localization.configValuesApp.apexEnabled &&
    internalHotelId &&
    origin &&
    adults &&
    nights &&
    localization.currency &&
    localization.businessProfile
      ? `apex-calendar-${scopeKey}`
      : undefined;

  const { data, isLoading } = useSWR<{ products: ApexCalendarProduct[] } | null>(
    key,
    async () => {
      const params = new URLSearchParams({
        hotelId: String(internalHotelId!),
        origin,
        adults: String(adults),
        nights: String(nights),
        userId: commonData.userUid || '',
        crn: localization.currency,
        bp: localization.businessProfile,
      });
      if (childrenAges) params.set('ages', childrenAges);
      if (apexFlightsCheapestEnabled) params.set('apexFlightsCheapest', '1');
      if (apexCacheMaxAge > 0) params.set('apexCacheMaxAge', String(apexCacheMaxAge));
      if (apexBestScoreTolerance != null) params.set('apexBestScoreTolerance', String(apexBestScoreTolerance));
      if (mealPlans.length) params.set('meal', mealPlans.join(','));
      if (flightTimesOutbound.length) params.set('flight_time_outbound', flightTimesOutbound.join(','));
      if (flightTimesInbound.length) params.set('flight_time_inbound', flightTimesInbound.join(','));

      return fetchWithTracingAndLogging<{ products: ApexCalendarProduct[] }>(
        `${BASE_PATH}/api/hotel-detail/apex-calendar?${params.toString()}`,
        { headers: { 'content-type': 'application/json' } },
        getLoggingInfo(pageType, 'apexCalendar', 'apexCalendarApiRoute'),
        searchInfoData.searchTraceParent,
        commonData
      );
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Rooms-corrected prices, kept in state so the calendar re-renders live (no refresh) as soon as a
  // day is reconciled below. Seeded from localStorage whenever the search scope changes so overrides
  // persisted within the 20-minute TTL survive reloads and remounts.
  const [overrides, setOverrides] = useState<Map<string, ApexPriceOverride>>(() => new Map());
  useEffect(() => {
    // Only rehydrate from storage when both overriding and its persistence are enabled.
    setOverrides(key && overrideEnabled && persistOverrides ? getApexPriceOverrides(scopeKey) : new Map());
  }, [key, scopeKey, overrideEnabled, persistOverrides]);

  useEffect(() => {
    if (!isLoading) {
      if (checkin && setPriceContext && data?.products) {
        const checkinKey = `${checkin.slice(0, 4)}-${checkin.slice(4, 6)}-${checkin.slice(6, 8)}`;
        const product = data.products.find((p) => p.datefrom === checkinKey);
        // A checkin match alone is not a stay match: the products were fetched for the calendar-filter
        // nights, so when the dates changed to a different stay length (sold-out alternative dates are
        // nights ±1) the found context prices another stay — the same cross-stay absorption MPH-904
        // removes elsewhere. Compared per product rather than against the filter so a product without
        // nights keeps seeding instead of silently disabling apex. Days rooms marked unavailable are
        // skipped too: their product survives in the raw response, and re-seeding its dead context on a
        // re-pick would undo the day-click reset just to burn the retry-without-context fallback.
        if (
          product?.context &&
          (product.nights == null || product.nights === stayNights) &&
          !overrides.get(checkinKey)?.unavailable
        ) {
          setPriceContext(product.context.price_context, product.context.signature, product.rates);
        }
      }
      setApexCalendarReady?.();
    }
  }, [isLoading, setApexCalendarReady, checkin, stayNights, setPriceContext, data, overrides]);

  // In DP the rooms cheapest price is per person, matching the apex product's price_per_person; its
  // total (all travellers) matches the apex product's total price.
  const roomsCheapestPerPerson = offersData?.cheapestMealPlan?.finalPrice;
  const roomsCheapestTotal = offersData?.cheapestMealPlan?.finalTotalPrice;
  const roomsResponseCode = offersData?.responseCode;

  // The selected day is genuinely sold out. Both guards match useReportHotelNoAvailability, for the
  // same reasons: `noAvailableRooms` is set for transient failures too (ERROR_FETCHING_ROOMS,
  // ERROR_PRICING_ASYNC, ERROR_PROCESSING_ROOMS_RESPONSE), and wiping the price off the calendar on a
  // rooms blip would be worse than showing it; and offersData survives similar-hotel detail->detail
  // navigation, so without the id match a stale sold-out would blank a day on the new hotel's
  // calendar before its own rooms response arrives.
  const selectedDaySoldOut =
    roomsResponseCode === 'ROOM_NOT_AVAILABLE' && offersData?.internalHotelId === internalHotelId;

  // Reconcile the apex price for the selected day with what the rooms endpoint actually found: the real
  // cheapest price, or no availability at all. Also covers days the apex calendar has no price for (the
  // "search" icon): they get the rooms price too.
  useEffect(() => {
    if (!key || !overrideEnabled || isLoadingRooms || !checkin) return;

    const dateKey = `${checkin.slice(0, 4)}-${checkin.slice(4, 6)}-${checkin.slice(6, 8)}`;

    // Sold out: record it so the calendar drops the apex price for this day and shows a
    // non-availability state instead. Before the price branch, which needs a price to work with —
    // a sold-out response carries none. (MPH-887)
    if (selectedDaySoldOut) {
      if (overrides.get(dateKey)?.unavailable) return;
      const stored = setApexPriceOverride(scopeKey, dateKey, { unavailable: true }, { persist: persistOverrides });
      setOverrides((prev) => new Map(prev).set(dateKey, stored));
      return;
    }

    if (roomsResponseCode !== 'OK' || !roomsCheapestPerPerson) return;

    const apexProduct = data?.products?.find((p) => p.datefrom === dateKey);
    const roomsTotal = roomsCheapestTotal || roomsCheapestPerPerson * totalGuestsWithoutInfants;

    // Apex already priced this day and it matches the rooms price: drop any stale override so the
    // original apex price shows again. (Not applicable to days with no apex price — nothing to revert to.)
    if (apexProduct && sameToCents(apexProduct.price_per_person, roomsCheapestPerPerson)) {
      if (overrides.has(dateKey)) {
        if (persistOverrides) removeApexPriceOverride(scopeKey, dateKey);
        setOverrides((prev) => {
          const next = new Map(prev);
          next.delete(dateKey);
          return next;
        });
      }
      return;
    }

    const existing = overrides.get(dateKey);
    if (
      existing &&
      !existing.unavailable &&
      sameToCents(existing.pricePerPerson, roomsCheapestPerPerson) &&
      sameToCents(existing.price, roomsTotal)
    ) {
      return;
    }

    // Update state unconditionally (drives the live re-render); persistence is toggled separately.
    const stored = setApexPriceOverride(
      scopeKey,
      dateKey,
      { pricePerPerson: roomsCheapestPerPerson, price: roomsTotal },
      { persist: persistOverrides }
    );
    setOverrides((prev) => new Map(prev).set(dateKey, stored));
  }, [
    key,
    overrideEnabled,
    persistOverrides,
    scopeKey,
    isLoadingRooms,
    roomsResponseCode,
    selectedDaySoldOut,
    roomsCheapestPerPerson,
    roomsCheapestTotal,
    totalGuestsWithoutInfants,
    checkin,
    data,
    overrides,
  ]);

  const { products, unavailableDates, cheapestPrice, currency } = useMemo(() => {
    const map = new Map<string, ApexCalendarProduct>();
    const unavailable = new Set<string>();
    let cur = '';

    (data?.products ?? []).forEach((product) => {
      if (product.datefrom) {
        map.set(product.datefrom, product);
        if (!cur && product.currency) cur = product.currency;
      }
    });

    // Apply the rooms-corrected prices on top of the apex data. For days the apex calendar has no
    // product for, synthesize one so the rooms price replaces the "search" icon.
    overrides.forEach((override, dateKey) => {
      // A sold-out day has no bookable price, so it must not keep the apex one: dropping it from the
      // map is what stops the price rendering, being picked as the month's cheapest, or feeding
      // `cheapestPrice`. The date is reported separately so the calendar can label it. (MPH-887)
      if (override.unavailable) {
        unavailable.add(dateKey);
        map.delete(dateKey);
        return;
      }
      const product = map.get(dateKey);
      if (product) {
        map.set(dateKey, { ...product, price_per_person: override.pricePerPerson, price: override.price });
      } else {
        map.set(dateKey, {
          datefrom: dateKey,
          checkin: dateKey,
          checkout: dateKey,
          nights,
          price: override.price,
          price_per_person: override.pricePerPerson,
          currency: cur || localization.currency,
        });
      }
    });

    let minPrice = Infinity;
    map.forEach((product) => {
      if (product.price_per_person < minPrice) minPrice = product.price_per_person;
    });

    return {
      products: map,
      unavailableDates: unavailable,
      cheapestPrice: minPrice === Infinity ? 0 : minPrice,
      currency: cur,
    };
  }, [data, overrides, nights, localization.currency]);

  return { products, unavailableDates, cheapestPrice, currency, isLoading };
}
